import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Tooltip,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LogoutIcon from '@mui/icons-material/Logout';
import AddBoxIcon from '@mui/icons-material/AddBox';
import TableViewIcon from '@mui/icons-material/TableView';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import { authService } from '../services/authService';
import { API_BASE_URL } from '../config';

const NAV_ITEMS = [
  { label: 'ثبت اطلاعات', shortLabel: 'ثبت', icon: AddBoxIcon, path: '/entry' },
  { label: 'نمایش اطلاعات', shortLabel: 'نمایش', icon: TableViewIcon, path: '/display' },
  { label: 'گزارش شرکت‌ها', shortLabel: 'گزارش', icon: BarChartIcon, path: '/reports', adminOnly: true },
  { label: 'مدیریت کاربران', shortLabel: 'مدیریت', icon: AdminPanelSettingsIcon, path: '/dashboard', adminOnly: true },
  { label: 'سوابق عملیات', shortLabel: 'سوابق', icon: HistoryIcon, path: '/dashboard?tab=audits', superAdminOnly: true },
];

const NavItem = ({ item, active, compact, onNavigate }) => {
  const Icon = item.icon;
  const button = (
    <Button
      onClick={() => onNavigate(item.path)}
      startIcon={<Icon sx={{ fontSize: compact ? 20 : 18 }} />}
      sx={{
        minWidth: compact ? 44 : 'auto',
        px: compact ? 1.25 : 2,
        py: 1,
        borderRadius: 2.5,
        fontWeight: active ? 800 : 600,
        fontSize: compact ? '0.8rem' : '0.875rem',
        color: active ? '#0073ea' : '#676879',
        bgcolor: active ? '#e5f4ff' : 'transparent',
        border: active ? '1px solid rgba(0, 168, 106, 0.25)' : '1px solid transparent',
        boxShadow: active ? '0 4px 14px rgba(0, 168, 106, 0.12)' : 'none',
        whiteSpace: 'nowrap',
        '&:hover': {
          bgcolor: active ? '#d8f3ee' : '#f7f8fa',
          color: active ? '#037f4c' : '#323338',
        },
        '& .MuiButton-startIcon': { ml: compact ? 0 : 1, mr: compact ? 0 : -0.5 },
      }}
    >
      {compact ? null : item.label}
    </Button>
  );

  if (compact) {
    return (
      <Tooltip title={item.label} arrow>
        <Box>{button}</Box>
      </Tooltip>
    );
  }
  return button;
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [user, setUser] = useState(() => authService.getCurrentUser() || {});

  useEffect(() => {
    const syncProfile = async () => {
      if (!authService.isAuthenticated()) return;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authService.setCurrentUser(response.data);
        setUser(response.data);
      } catch {
        setUser(authService.getCurrentUser() || {});
      }
    };
    syncProfile();
  }, [location.pathname]);

  const handleLogout = () => {
    authService.clearAuth();
    navigate('/login');
  };

  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (item.superAdminOnly) return user.is_super_admin;
    if (item.adminOnly) return user.role === 'admin';
    return true;
  });

  const isNavActive = (path) => {
    if (path.includes('?tab=audits')) {
      return location.pathname === '/dashboard' && new URLSearchParams(location.search).get('tab') === 'audits';
    }
    return location.pathname === path;
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        mb: { xs: 2, md: 3 },
        bgcolor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(12px)',
        color: '#323338',
        borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
        boxShadow: '0 4px 24px rgba(50, 51, 56, 0.06)',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 1, md: 1.25 },
          gap: { xs: 1, md: 2 },
          minHeight: { xs: 60, md: 68 },
        }}
      >
        {/* Logo + brand */}
        <Box
          onClick={() => navigate(user.role === 'admin' ? '/reports' : '/display')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            cursor: 'pointer',
            flexShrink: 0,
            pr: { md: 1 },
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--color-primary)',
              boxShadow: '0 6px 20px rgba(50, 51, 56, 0.3)',
            }}
          >
            <img src="/logo.png" alt="Logo" style={{ height: 26, width: 26, objectFit: 'contain' }} />
          </Box>
          {!isXs && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#323338', lineHeight: 1.2 }}>
                سامانه ERP
              </Typography>
              <Typography variant="caption" sx={{ color: '#c5c7d0', fontWeight: 600 }}>
                مدیریت اطلاعات
              </Typography>
            </Box>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: '#e6e9ef' }} />

        {/* Navigation */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.5, sm: 0.75, md: 1 },
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            px: { xs: 0.5, md: 1 },
          }}
        >
          {visibleNavItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              active={isNavActive(item.path)}
              compact={isSm}
              onNavigate={navigate}
            />
          ))}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: '#e6e9ef' }} />

        {/* Logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isXs ? (
            <Tooltip title="خروج" arrow>
              <IconButton
                onClick={handleLogout}
                sx={{
                  bgcolor: '#fde8eb',
                  color: '#c9364a',
                  border: '1px solid #f5b8c0',
                  '&:hover': { bgcolor: '#fde8eb' },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2.5,
                fontWeight: 700,
                color: '#c9364a',
                bgcolor: '#fde8eb',
                border: '1px solid #f5b8c0',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#fde8eb', borderColor: '#f5b8c0' },
                '& .MuiButton-startIcon': { ml: 1 },
              }}
            >
              خروج
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
